import { useCallback, useEffect, useRef, useState } from 'react';

const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

const useVoiceRecorder = ({ maxBytes = Infinity } = {}) => {
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState('');
  const [completedBlob, setCompletedBlob] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [quietInput, setQuietInput] = useState(false);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const animationFrameRef = useRef(null);
  const timerRef = useRef(null);
  const sizeLimitReachedRef = useRef(false);

  const stopMonitoring = useCallback(() => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    animationFrameRef.current = null;
    timerRef.current = null;
    audioContextRef.current?.close?.().catch?.(() => {});
    audioContextRef.current = null;
    setAudioLevel(0);
    setQuietInput(false);
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => () => {
    stopMonitoring();
    stopStream();
  }, [stopMonitoring, stopStream]);

  const startMonitoring = useCallback((stream) => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    const startedAt = Date.now();
    let lastSoundAt = startedAt;
    let lastPaintAt = 0;

    setAudioLevel(0);
    setElapsedSeconds(0);
    setQuietInput(false);
    timerRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);

    if (!AudioContextClass) return;
    const audioContext = new AudioContextClass();
    audioContext.resume?.().catch?.(() => {});
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    audioContext.createMediaStreamSource(stream).connect(analyser);
    audioContextRef.current = audioContext;
    const samples = new Uint8Array(analyser.fftSize);

    const updateLevel = (paintTime = 0) => {
      analyser.getByteTimeDomainData(samples);
      let energy = 0;
      samples.forEach((sample) => {
        const centered = (sample - 128) / 128;
        energy += centered * centered;
      });
      const level = Math.min(1, Math.sqrt(energy / samples.length) * 4.5);
      const now = Date.now();
      if (level > 0.035) lastSoundAt = now;
      if (paintTime - lastPaintAt >= 80) {
        setAudioLevel(level);
        setQuietInput(now - startedAt >= 5000 && now - lastSoundAt >= 5000);
        lastPaintAt = paintTime;
      }
      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };
    animationFrameRef.current = requestAnimationFrame(updateLevel);
  }, []);

  const start = useCallback(async () => {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Voice recording is not supported in this browser. You can upload an audio file instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      chunksRef.current = [];
      sizeLimitReachedRef.current = false;
      setCompletedBlob(null);
      startMonitoring(stream);
      recorder.ondataavailable = (event) => {
        if (!event.data?.size) return;
        const recordedBytes = chunksRef.current.reduce((total, chunk) => total + chunk.size, 0);
        if (sizeLimitReachedRef.current || recordedBytes + event.data.size > maxBytes) {
          sizeLimitReachedRef.current = true;
          setError('The recording reached the 20 MB limit and was stopped.');
          if (recorder.state === 'recording') recorder.stop();
          return;
        }
        chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const completed = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        if (completed.size <= maxBytes) setCompletedBlob(completed);
        else setError('The recording reached the 20 MB limit and was not saved.');
        setRecording(false);
        stopMonitoring();
        stopStream();
      };
      recorder.start(1000);
      setRecording(true);
    } catch (nextError) {
      stopMonitoring();
      stopStream();
      setError(nextError?.name === 'NotAllowedError'
        ? 'Microphone access was blocked. Allow access or upload an audio file instead.'
        : 'We could not start the microphone.');
    }
  }, [maxBytes, startMonitoring, stopMonitoring, stopStream]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      return;
    }
    recorder.stop();
  }, []);

  const clearCompletedBlob = useCallback(() => setCompletedBlob(null), []);

  return { recording, error, completedBlob, audioLevel, elapsedSeconds, quietInput, clearCompletedBlob, start, stop };
};

export default useVoiceRecorder;
